import React, { useState, useCallback, useEffect } from "react";
import { StepContainer, Step } from "newui/components/Stepper";
import { useCurrentCompanyId } from "hooks/auth";
import { useQuery } from "@tanstack/react-query";
import { StaleTime } from "newui/store/tanstack-base";
import { useRatingWorkflow } from "../../context/RatingWorkflowContext";
import { useCreateRating } from "../../hooks/useCreateRating";
import { SelectSupplierStep } from "./steps/SelectSupplierStep";
import { SelectProjectStep } from "./steps/SelectProjectStep";
import { SelectFrequencyStep } from "./steps/SelectFrequencyStep";
import { SelectCategoriesStep } from "./steps/SelectCategoriesStep";
import { RateCategoriesStep } from "./steps/RateCategoriesStep";
import { SummaryStep } from "./steps/SummaryStep";
import {
	getMySuppliers,
	getAwardedProjects,
	getPerformanceCategories,
} from "newui/features/MySuppliers/api/supplier-performance-api";
import { SUPPLIER_PERFORMANCE_QUERY_KEYS } from "newui/features/MySuppliers/constants/query-keys";
import type {
	AwardedSupplier,
	CreateRatingPayload,
} from "newui/features/MySuppliers/types/supplier-performance.types";

interface RatingWorkflowProps {
	onComplete: () => void;
}

export const RatingWorkflow: React.FC<RatingWorkflowProps> = ({
	onComplete,
}) => {
	const companyId = useCurrentCompanyId();
	const { state, dispatch } = useRatingWorkflow();
	const [supplierSearch, setSupplierSearch] = useState("");

	const { mutate: submitRating, isPending: isSubmitting } = useCreateRating({
		onSuccess: () => {
			onComplete();
		},
	});

	// Fetch suppliers for step 1
	const { data: suppliersData, isLoading: isSuppliersLoading } = useQuery({
		queryKey: SUPPLIER_PERFORMANCE_QUERY_KEYS.mySuppliers(
			companyId ?? 0,
			{ search: supplierSearch }
		),
		queryFn: () =>
			getMySuppliers(companyId!, {
				search: supplierSearch,
				supplierType: [],
				performanceBand: [],
				isActive: null,
				sortBy: "name",
				sortOrder: "asc",
				page: 1,
				perPage: 50,
			}),
		enabled: companyId !== null,
		staleTime: StaleTime.Short,
	});

	const suppliers: AwardedSupplier[] = suppliersData?.data ?? [];

	// Fetch awarded projects for step 2
	const { data: awardedProjects = [], isLoading: isProjectsLoading } =
		useQuery({
			queryKey: SUPPLIER_PERFORMANCE_QUERY_KEYS.awardedProjects(
				companyId ?? 0,
				state.supplierId ?? 0
			),
			queryFn: () =>
				getAwardedProjects(companyId!, state.supplierId!),
			enabled: companyId !== null && state.supplierId !== null,
			staleTime: StaleTime.Short,
		});

	// Fetch categories for step 4 and 5
	const { data: categories = [], isLoading: isCategoriesLoading } =
		useQuery({
			queryKey: SUPPLIER_PERFORMANCE_QUERY_KEYS.performanceCategories(),
			queryFn: getPerformanceCategories,
			staleTime: StaleTime.Long,
		});

	// Populate supplier name when pre-selected via URL and suppliers load
	useEffect(() => {
		if (state.supplierId && !state.supplierName && suppliers.length > 0) {
			const match = suppliers.find(
				(s) => s.supplier_company_id === state.supplierId
			);
			if (match) {
				dispatch({
					type: "SET_SUPPLIER",
					payload: {
						supplierId: match.supplier_company_id,
						supplierName: match.company_name,
					},
				});
			}
		}
	}, [state.supplierId, state.supplierName, suppliers, dispatch]);

	const handleSearchChange = useCallback((search: string) => {
		setSupplierSearch(search);
	}, []);

	const handleStepChange = useCallback(
		async (_step: number) => {
			// no-op for now; draft saving not yet wired
		},
		[]
	);

	const handleSubmit = useCallback(() => {
		if (!state.supplierId) {
			return;
		}

		submitRating({
			createPayload: {
				supplier_company_id: state.supplierId,
				rfx_invite_id: state.rfxInviteId,
				rfx_id: state.rfxId,
				rfq_project_id: state.projectId,
				rating_level: state.reviewLevel,
				frequency_type: state.frequency,
			},
			selectedCategoryIds: state.selectedCategoryIds,
			ratings: state.ratings,
		});
	}, [state, submitRating]);

	const isStep1Valid = state.supplierId !== null;
	const isStep2Valid =
		state.projectId !== null || state.rfxId !== null;
	const isStep3Valid = true; // frequency always has a value
	const isStep4Valid = state.selectedCategoryIds.length > 0;

	// Step 5 valid when all sub-criteria in selected categories have scores
	const selectedCats = categories.filter((cat) =>
		state.selectedCategoryIds.includes(cat.id)
	);
	const isStep5Valid = selectedCats.every((cat) =>
		cat.sub_criteria.every((sub) => {
			const rating = state.ratings[cat.id]?.find(
				(r) => r.subCriterionId === sub.id
			);
			return rating && rating.score > 0;
		})
	);

	return (
		<div data-testid="rating-workflow">
			<StepContainer
				onComplete={handleSubmit}
				onStepChange={handleStepChange}
				isSubmitting={isSubmitting}
			>
				<Step
					name="Select Supplier"
					description="Choose the supplier to rate"
					enabled={isStep1Valid}
				>
					<SelectSupplierStep
						suppliers={suppliers}
						isLoading={isSuppliersLoading}
						onSearchChange={handleSearchChange}
					/>
				</Step>

				<Step
					name="Select Project"
					description="Choose the project or RFx"
					enabled={isStep2Valid}
				>
					<SelectProjectStep
						awardedProjects={awardedProjects}
						isLoading={isProjectsLoading}
					/>
				</Step>

				<Step
					name="Review Frequency"
					description="One-off or periodic"
					enabled={isStep3Valid}
				>
					<SelectFrequencyStep />
				</Step>

				<Step
					name="KPI Categories"
					description="Select categories to evaluate"
					enabled={isStep4Valid}
				>
					<SelectCategoriesStep
						categories={categories}
						isLoading={isCategoriesLoading}
					/>
				</Step>

				<Step
					name="Rate Performance"
					description="Score each sub-criterion"
					enabled={isStep5Valid}
				>
					<RateCategoriesStep categories={categories} />
				</Step>

				<Step
					name="Summary"
					description="Review and submit"
					enabled={isStep5Valid}
					nextLabel="Submit"
				>
					<SummaryStep categories={categories} />
				</Step>
			</StepContainer>
		</div>
	);
};
